// frontend/src/tour/steps/leads-mobile-map.steps.js
// Mobile-only tour for LeadsMobileMapView filter FABs.

function normalizeRole(role) {
  if (!role) return "Sales Rep";
  const roleLower = role.toLowerCase();
  if (roleLower.includes("admin")) return "Admin";
  if (roleLower.includes("director")) return "Admin";
  if (roleLower.includes("manager")) return "Manager";
  return "Sales Rep";
}

export const LEADS_MOBILE_MAP_TOUR_STEPS = (role) => {
  const normalizedRole = normalizeRole(role);
  const isManagerOrAdmin = ["Admin", "Manager"].includes(normalizedRole);

  const steps = [
    {
      id: "mobile-map-welcome",
      selector: "body",
      placement: "center",
      copy: {
        Admin: {
          title: "Mobile Map Filters",
          content:
            "On mobile you can filter the map with these controls. Use Lead Status, Lead Date, and Territories to focus on the leads you need.",
        },
        Manager: {
          title: "Mobile Map Filters",
          content:
            "Use these filter buttons to narrow the map: Lead Status, Lead Date, and Territories. They help you focus on the right leads on the go.",
        },
        "Sales Rep": {
          title: "Mobile Map Filters",
          content:
            "Filter the map with these controls: Lead Status and Lead Date. Tap each button to see only the leads you need.",
        },
      },
    },
    {
      id: "mobile-map-lead-status-filter",
      selector: '[data-tour="mobile-map-lead-status-filter"]',
      placement: "top",
      copy: {
        Admin: {
          title: "Lead Status",
          content:
            "Tap to show the status legend. Tap a status (e.g. New, Follow Up, ADT, Title Co, Builder) to show only those leads on the map. Tap again to deselect. Use Clear All to reset.",
        },
        Manager: {
          title: "Lead Status",
          content:
            "Tap to show the status legend. Tap a status (e.g. New, Follow Up, ADT, Title Co, Builder) to show only those leads on the map. Tap again to deselect. Use Clear All to reset.",
        },
        "Sales Rep": {
          title: "Lead Status",
          content:
            "Tap to show the status legend. Tap a status (e.g. New, Follow Up, ADT, Title Co, Builder) to show only those leads on the map. Tap again to deselect. Use Clear All to reset.",
        },
      },
    },
    {
      id: "mobile-map-lead-date-filter",
      selector: '[data-tour="mobile-map-lead-date-filter"]',
      placement: "top",
      copy: {
        Admin: {
          title: "Lead Date",
          content:
            "Filter leads by upload or sold date. Choose a preset (e.g. last 3 upload dates) or Custom Date Range to set start and end. Use Clear Filter to show all dates.",
        },
        Manager: {
          title: "Lead Date",
          content:
            "Filter leads by upload or sold date. Choose a preset (e.g. last 3 upload dates) or Custom Date Range to set start and end. Use Clear Filter to show all dates.",
        },
        "Sales Rep": {
          title: "Lead Date",
          content:
            "Filter leads by upload or sold date. Choose a preset (e.g. last 3 upload dates) or Custom Date Range to set start and end. Use Clear Filter to show all dates.",
        },
      },
    },
  ];

  // Lead Detail Drawer — tap pin intro
  steps.push({
    id: "mobile-lead-drawer-intro",
    selector: "body",
    placement: "center",
    copy: {
      Admin: {
        title: "Lead Detail Sheet",
        content:
          "Tap any pin on the map to open the Lead Detail sheet from the bottom of the screen. It shows the lead's name, address, status, and quick-action buttons — all without leaving the map.",
      },
      Manager: {
        title: "Lead Detail Sheet",
        content:
          "Tap a map pin to pull up the Lead Detail sheet. See the lead's key info and take action — add to route, navigate, or view full details — right from the map.",
      },
      "Sales Rep": {
        title: "Lead Detail Sheet",
        content:
          "Tap any pin and a sheet slides up from the bottom with that lead's info. Add it to your route, navigate to it, or tap Lead Details to see and edit everything.",
      },
    },
  });

  // Add to Route (mobile)
  steps.push({
    id: "mobile-drawer-add-route",
    selector: "body",
    placement: "center",
    copy: {
      Admin: {
        title: "Add to Route",
        content:
          "The 'Add to Route' button in the sheet adds the lead to your route builder. The sheet closes automatically so you can keep selecting leads. Already added? The button switches to 'Remove from Route'.",
      },
      Manager: {
        title: "Add to Route",
        content:
          "Tap 'Add to Route' to queue the lead for your route. The sheet closes and the lead is saved to your route. Tap 'Remove from Route' on the same button to undo.",
      },
      "Sales Rep": {
        title: "Add to Route",
        content:
          "Tap 'Add to Route' to add the lead to your route. The sheet closes so you can keep tapping pins. The button says 'Remove from Route' if it's already been added.",
      },
    },
  });

  // Lead Details expand button (mobile)
  steps.push({
    id: "mobile-drawer-lead-details-btn",
    selector: "body",
    placement: "center",
    copy: {
      Admin: {
        title: "Lead Details & Edit",
        content:
          "Tap 'Lead Details' to expand the sheet and see all lead information — contact details, stats, notes, and edit fields. Update the lead's name, phone, status, or notes and save, all from the map.",
      },
      Manager: {
        title: "Lead Details & Edit",
        content:
          "Tap 'Lead Details' to expand the sheet and access full lead info and edit fields. Make changes to status, contact details, or notes and save without navigating away.",
      },
      "Sales Rep": {
        title: "Lead Details & Edit",
        content:
          "Tap 'Lead Details' to expand the sheet and edit the lead's info — name, phone, status, notes. Hit Save when done and you're back on the map.",
      },
    },
  });

  if (isManagerOrAdmin) {
    steps.push({
      id: "mobile-map-territory-filter",
      selector: '[data-tour="mobile-map-territory-filter"]',
      placement: "top",
      copy: {
        Admin: {
          title: "Territories",
          content:
            "Filter the map to leads in selected company territories. Tap to open the list, then select or deselect territories. Only leads in the selected territories will appear on the map.",
        },
        Manager: {
          title: "Territories",
          content:
            "Filter the map to leads in your selected territories. Tap to open the list, then select or deselect territories. Only leads in the selected territories will appear on the map.",
        },
        "Sales Rep": {
          title: "Territories",
          content:
            "Filter the map to leads in selected territories. Tap to open the list, then select or deselect territories. Only leads in the selected territories will appear on the map.",
        },
      },
    });
  }

  return steps.map((step) => {
    const roleCopy = step.copy[normalizedRole] || step.copy["Sales Rep"];
    return {
      selector: step.selector,
      placement: step.placement,
      title: roleCopy.title,
      content: roleCopy.content,
    };
  });
};

/**
 * Versioned "new only" tour for Leads Map (mobile).
 * Add steps here when you ship new features; bump the key (e.g. v1_7_leads_mobile_map_new_complete) when you add more.
 */
export const LEADS_MOBILE_MAP_NEW_STEPS = () => [];
